
import { useState, useEffect } from "react";
import { ColumnMapping } from "@/types/import";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";

interface ColumnMappingFormProps {
  columns: string[];
  mapping: ColumnMapping;
  onChange: (mapping: ColumnMapping) => void;
}

export const ColumnMappingForm = ({
  columns,
  mapping,
  onChange,
}: ColumnMappingFormProps) => {
  const [localMapping, setLocalMapping] = useState<ColumnMapping>(mapping);

  useEffect(() => {
    setLocalMapping(mapping);
  }, [mapping]);

  const handleMappingChange = (field: keyof ColumnMapping, value: string) => {
    const updatedMapping = {
      ...localMapping,
      [field]: value,
    };
    setLocalMapping(updatedMapping);
    onChange(updatedMapping);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="content-column">Quote Content <span className="text-red-500">*</span></Label>
          <Select
            value={localMapping.content}
            onValueChange={(value) => handleMappingChange("content", value)}
          >
            <SelectTrigger id="content-column">
              <SelectValue placeholder="Select a column" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="not-mapped">Not mapped</SelectItem>
              {columns.map((column) => (
                <SelectItem key={column} value={column}>
                  {column}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="author-column">Author</Label>
          <Select
            value={localMapping.author}
            onValueChange={(value) => handleMappingChange("author", value)}
          >
            <SelectTrigger id="author-column">
              <SelectValue placeholder="Select a column" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="not-mapped">Not mapped</SelectItem>
              {columns.map((column) => (
                <SelectItem key={column} value={column}>
                  {column}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="source-column">Source</Label>
          <Select
            value={localMapping.source}
            onValueChange={(value) => handleMappingChange("source", value)}
          >
            <SelectTrigger id="source-column">
              <SelectValue placeholder="Select a column" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="not-mapped">Not mapped</SelectItem>
              {columns.map((column) => (
                <SelectItem key={column} value={column}>
                  {column}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="categories-column">Categories</Label>
          <Select
            value={localMapping.categories}
            onValueChange={(value) => handleMappingChange("categories", value)}
          >
            <SelectTrigger id="categories-column">
              <SelectValue placeholder="Select a column" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="not-mapped">Not mapped</SelectItem>
              {columns.map((column) => (
                <SelectItem key={column} value={column}>
                  {column}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="category-delimiter">Category Delimiter</Label>
        <Input
          id="category-delimiter"
          value={localMapping.categoryDelimiter}
          onChange={(e) =>
            handleMappingChange("categoryDelimiter", e.target.value)
          }
          placeholder="Delimiter for multiple categories (e.g., comma)"
        />
        <p className="text-sm text-muted-foreground">
          Character that separates multiple categories in a single field (default: comma)
        </p>
      </div>
    </div>
  );
};
